import React, { useEffect, useMemo, useState } from 'react';
import FitTable from './FitTable';
import PlayerName from './PlayerName';
import CellEditor, { CellValue } from './CellEditor';
import { useIsMobile } from '../hooks/useMediaQuery';
import {
  ALL_HOLES,
  Cell,
  Nine,
  nineHoles,
  playedNines,
  rowTotals,
  scoreKind,
  sumHoles,
} from '../utils/holeScores';
import '../css/scorecard.css';

export type CellState = 'low' | 'flag' | 'resolved' | 'edited';

export interface GridRow {
  key: string;
  first?: string | null;
  last?: string | null;
  /** Shown instead of first/last (e.g. the name written on a card). */
  label?: string | null;
  subtitle?: React.ReactNode;
  warning?: string | null;
  strokes: Cell[];
  putts?: Cell[];
  penalties?: Cell[];
  /** hole → review markers (low confidence, flagged, fixed by the card's total, edited) */
  cellState?: Record<number, CellState[]>;
  /** OUT / IN totals that don't match the card (shown in red). */
  sumFlags?: { out?: boolean; in?: boolean };
  muted?: boolean;
  /** Per-row override of the grid's `editable`. */
  editable?: boolean;
}

export interface ActiveCell { rowKey: string; hole: number }

interface HoleByHoleGridProps {
  pars: Cell[];
  rows: GridRow[];
  playedHoles?: number[];
  editable?: boolean;
  /** Show putts / penalty strokes under each score (and in the editor). */
  showExtras?: boolean;
  onCellChange?: (rowKey: string, hole: number, value: CellValue) => void | Promise<void>;
  onCellSelect?: (rowKey: string, hole: number) => void;
  activeCell?: ActiveCell | null;
  /** 'auto' = holes across on desktop, holes down on phones. */
  layout?: 'auto' | 'horizontal' | 'vertical';
  /**
   * Control which cell's editor is open (the review page opens problem holes
   * itself). Without `onEditingChange` the grid manages this on its own.
   */
  editing?: ActiveCell | null;
  onEditingChange?: (cell: ActiveCell | null) => void;
  /** What's wrong with a hole, shown in its editor. */
  issuesFor?: (rowKey: string, hole: number) => React.ReactNode;
  /** Where "save and go on" goes (default: the next hole), and its label. */
  nextCell?: (rowKey: string, hole: number) => ActiveCell | null;
  nextLabel?: string;
  /** Open the editor in part of the screen only (see CellEditor `area`). */
  editorArea?: () => { top?: number; left?: number } | null;
}

const rowName = (row: GridRow) => row.label || `${row.first ?? ''} ${row.last ?? ''}`.trim() || '—';

/** Column labels for the phone layout: first names, disambiguated when shared. */
function shortLabels(rows: GridRow[]): Map<string, string> {
  const first = (r: GridRow) => (r.label || r.first || rowName(r)).split(' ')[0];
  const counts = new Map<string, number>();
  rows.forEach((r) => counts.set(first(r), (counts.get(first(r)) || 0) + 1));
  return new Map(rows.map((r) => {
    const f = first(r);
    const dup = (counts.get(f) || 0) > 1 && r.last;
    return [r.key, dup ? `${f} ${String(r.last).charAt(0)}.` : f];
  }));
}

/**
 * The hole-by-hole scorecard. Desktop: the classic card (holes across, OUT /
 * IN / TOT). Phone: one nine at a time, holes down and players across, so it
 * fits without sideways scrolling. Scores get the golf marks (birdie ○,
 * bogey □); tapping an editable cell opens the cell editor.
 */
const HoleByHoleGrid: React.FC<HoleByHoleGridProps> = ({
  pars,
  rows,
  playedHoles = ALL_HOLES,
  editable = false,
  showExtras = false,
  onCellChange,
  onCellSelect,
  activeCell = null,
  layout = 'auto',
  editing: editingProp,
  onEditingChange,
  issuesFor,
  nextCell,
  nextLabel,
  editorArea,
}) => {
  const isMobile = useIsMobile();
  const vertical = layout === 'vertical' || (layout === 'auto' && isMobile);
  const nines = playedNines(playedHoles);
  const [nine, setNine] = useState<Nine>(nines[0] || 'front');
  const [editingOwn, setEditingOwn] = useState<ActiveCell | null>(null);
  const controlled = !!onEditingChange;
  const editing = controlled ? editingProp ?? null : editingOwn;
  const setEditing = (c: ActiveCell | null) => (controlled ? onEditingChange!(c) : setEditingOwn(c));
  // Measured when an editor opens (the photo may have been resized since).
  const area = useMemo(() => (editing && editorArea ? editorArea() : null), [editing?.rowKey, editing?.hole]); // eslint-disable-line react-hooks/exhaustive-deps
  const labels = useMemo(() => shortLabels(rows), [rows]);

  // Follow the active / edited cell to its nine (e.g. a flag picked on the review page).
  const follow = editing || activeCell;
  useEffect(() => {
    if (!follow) return;
    const n: Nine = follow.hole <= 9 ? 'front' : 'back';
    if (nines.includes(n)) setNine(n);
  }, [follow?.rowKey, follow?.hole]); // eslint-disable-line react-hooks/exhaustive-deps

  // A dot on the nine toggle when that nine has something to fix.
  const nineHasFlag = (n: Nine) => rows.some((r) => (n === 'front' ? r.sumFlags?.out : r.sumFlags?.in)
    || nineHoles(n, playedHoles).some((h) => (r.cellState?.[h] || []).includes('flag')));

  const canEdit = (row: GridRow) => (row.editable ?? editable) && !!onCellChange;
  const onCell = (row: GridRow, hole: number) => {
    onCellSelect?.(row.key, hole);
    if (canEdit(row)) setEditing({ rowKey: row.key, hole });
  };

  const cellClass = (row: GridRow, hole: number) => {
    const states = row.cellState?.[hole] || [];
    const s = row.strokes[hole - 1];
    return [
      'sc-cell',
      canEdit(row) ? 'sc-cell--editable' : '',
      s == null ? 'sc-cell--empty' : '',
      ...states.map((x) => `sc-cell--${x}`),
      activeCell && activeCell.rowKey === row.key && activeCell.hole === hole ? 'sc-cell--active' : '',
    ].filter(Boolean).join(' ');
  };

  const renderScore = (row: GridRow, hole: number) => {
    const s = row.strokes[hole - 1];
    const par = pars[hole - 1];
    const kind = scoreKind(s, par);
    const putts = row.putts?.[hole - 1];
    const pens = row.penalties?.[hole - 1];
    const extras = showExtras && (putts != null || (pens != null && pens > 0));
    return (
      <>
        <span className={`sc-score${kind && kind !== 'par' ? ` sc-score--${kind}` : ''}`}>{s ?? (canEdit(row) ? '+' : '·')}</span>
        {extras && (
          <span className="sc-extra">
            {putts != null ? `${putts}p` : ''}
            {pens != null && pens > 0 && <span className="sc-pen" title={`${pens} penalty stroke${pens > 1 ? 's' : ''}`}>{' '}{'•'.repeat(Math.min(pens, 3))}</span>}
          </span>
        )}
      </>
    );
  };

  const cellProps = (row: GridRow, hole: number) => ({
    className: cellClass(row, hole),
    onClick: () => onCell(row, hole),
    role: canEdit(row) || onCellSelect ? 'button' : undefined,
    tabIndex: canEdit(row) || onCellSelect ? 0 : undefined,
    'aria-label': `${rowName(row)}, hole ${hole}: ${row.strokes[hole - 1] ?? 'no score'}`,
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCell(row, hole); } },
  });

  const parSum = (holes: number[]) => sumHoles(pars, holes);
  const front = nineHoles('front', playedHoles);
  const back = nineHoles('back', playedHoles);

  const horizontal = (
    <FitTable>
      <table className="tbl tbl--grid sc-grid">
        <thead>
          <tr>
            <th className="sc-name">Hole</th>
            {front.map((h) => <th key={h}>{h}</th>)}
            {front.length > 0 && <th className="sc-sum">OUT</th>}
            {back.map((h) => <th key={h}>{h}</th>)}
            {back.length > 0 && <th className="sc-sum">IN</th>}
            <th className="sc-sum">TOT</th>
          </tr>
        </thead>
        <tbody>
          <tr className="sc-par">
            <td className="sc-name">Par</td>
            {front.map((h) => <td key={h}>{pars[h - 1] ?? '–'}</td>)}
            {front.length > 0 && <td className="sc-sum">{parSum(front) ?? '–'}</td>}
            {back.map((h) => <td key={h}>{pars[h - 1] ?? '–'}</td>)}
            {back.length > 0 && <td className="sc-sum">{parSum(back) ?? '–'}</td>}
            <td className="sc-sum">{parSum(playedHoles) ?? '–'}</td>
          </tr>
          {rows.map((row) => {
            const t = rowTotals(row.strokes, playedHoles);
            return (
              <tr key={row.key} className={row.muted ? 'sc-row--muted' : undefined}>
                <td className="sc-name">
                  {row.label ? row.label : <PlayerName first={row.first} last={row.last} />}
                  {row.subtitle && <span className="sc-sub">{row.subtitle}</span>}
                  {row.warning && <span className="sc-warn">⚠ {row.warning}</span>}
                </td>
                {front.map((h) => <td key={h} {...cellProps(row, h)}>{renderScore(row, h)}</td>)}
                {front.length > 0 && <td className={`sc-sum${row.sumFlags?.out ? ' sc-sum--flag' : ''}`}>{t.out ?? ''}</td>}
                {back.map((h) => <td key={h} {...cellProps(row, h)}>{renderScore(row, h)}</td>)}
                {back.length > 0 && <td className={`sc-sum${row.sumFlags?.in ? ' sc-sum--flag' : ''}`}>{t.in ?? ''}</td>}
                <td className="sc-sum">{t.total ?? ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </FitTable>
  );

  const holesShown = nineHoles(nine, playedHoles);
  const verticalView = (
    <>
      {nines.length > 1 && (
        <div className="sc-toolbar">
          <div className="sc-seg" role="group" aria-label="Nine">
            <button type="button" aria-pressed={nine === 'front'} onClick={() => setNine('front')}>
              Front 9{nineHasFlag('front') && <span className="sc-seg-dot" title="Has holes to fix" />}
            </button>
            <button type="button" aria-pressed={nine === 'back'} onClick={() => setNine('back')}>
              Back 9{nineHasFlag('back') && <span className="sc-seg-dot" title="Has holes to fix" />}
            </button>
          </div>
        </div>
      )}
      <FitTable>
        <table className="tbl tbl--grid sc-grid sc-grid--vertical">
          <thead>
            <tr>
              <th>Hole</th>
              <th className="sc-parcol">Par</th>
              {rows.map((row) => (
                <th key={row.key} className="sc-player" title={rowName(row)}>{labels.get(row.key)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {holesShown.map((h) => (
              <tr key={h}>
                <td>{h}</td>
                <td className="sc-parcol">{pars[h - 1] ?? '–'}</td>
                {rows.map((row) => <td key={row.key} {...cellProps(row, h)}>{renderScore(row, h)}</td>)}
              </tr>
            ))}
            <tr>
              <td className="sc-sum">{nine === 'front' ? 'OUT' : 'IN'}</td>
              <td className="sc-sum sc-parcol">{parSum(holesShown) ?? '–'}</td>
              {rows.map((row) => (
                <td key={row.key} className={`sc-sum${(nine === 'front' ? row.sumFlags?.out : row.sumFlags?.in) ? ' sc-sum--flag' : ''}`}>
                  {sumHoles(row.strokes, holesShown) ?? ''}
                </td>
              ))}
            </tr>
            {nines.length > 1 && (
              <tr>
                <td className="sc-sum">TOT</td>
                <td className="sc-sum sc-parcol">{parSum(playedHoles) ?? '–'}</td>
                {rows.map((row) => <td key={row.key} className="sc-sum">{rowTotals(row.strokes, playedHoles).total ?? ''}</td>)}
              </tr>
            )}
          </tbody>
        </table>
      </FitTable>
      {rows.some((r) => r.warning) && (
        <ul className="sc-note" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
          {rows.filter((r) => r.warning).map((r) => <li key={r.key}><b>{rowName(r)}</b>: {r.warning}</li>)}
        </ul>
      )}
    </>
  );

  const editRow = editing ? rows.find((r) => r.key === editing.rowKey) : null;
  const nextHole = editing ? playedHoles.find((h) => h > editing.hole) ?? null : null;
  const next: ActiveCell | null = !editing ? null
    : nextCell ? nextCell(editing.rowKey, editing.hole)
    : nextHole != null ? { rowKey: editing.rowKey, hole: nextHole } : null;

  return (
    <div className="sc-grid-wrap">
      {vertical ? verticalView : horizontal}
      {editing && editRow && (
        <CellEditor
          key={`${editing.rowKey}:${editing.hole}`}
          title={`${rowName(editRow)} · Hole ${editing.hole}`}
          subtitle={pars[editing.hole - 1] != null ? `Par ${pars[editing.hole - 1]}` : undefined}
          par={(pars[editing.hole - 1] as number | null) ?? null}
          value={{
            strokes: (editRow.strokes[editing.hole - 1] as number | null) ?? null,
            putts: (editRow.putts?.[editing.hole - 1] as number | null) ?? null,
            penalties: (editRow.penalties?.[editing.hole - 1] as number | null) ?? null,
          }}
          showExtras={showExtras}
          hasNext={next != null}
          nextLabel={nextLabel}
          issues={issuesFor?.(editing.rowKey, editing.hole)}
          area={area}
          onClose={() => setEditing(null)}
          onSave={async (value, goOn) => {
            const at = editing;
            setEditing(goOn && next ? next : null);
            await onCellChange?.(at.rowKey, at.hole, value);
          }}
        />
      )}
    </div>
  );
};

export default HoleByHoleGrid;
